import React from "react";
import { Link, useLocation } from "react-router-dom";
import "./Sidebar.css";
import { FaCog, FaTasks } from "react-icons/fa";
import { File, Network } from "lucide-react";
import {
  DashboardIcon,
  DataSourceIcon,
  MappingIcon,
} from "../../assets/icon/svgIcons";
import MyLogo from "../../assets/chatbot.png";
import PropTypes from "prop-types";

// Helper function to get flex direction class
const getFlexDirectionClass = (isOpen) => {
  return isOpen === false ? "flex-col" : "flex-row";
};

// Helper function to check if path is active
const isPathActive = (location, paths) => {
  if (Array.isArray(paths)) {
    return paths.some((path) => location.pathname.includes(path));
  }
  return location.pathname === paths || location.pathname.includes(paths);
};

// Menu item component to reduce cognitive complexity
const MenuItem = ({ isOpen, location, path, paths, title, icon, label, className, joyrideClass }) => {
  const flexClass = getFlexDirectionClass(isOpen);
  const activeClass = isPathActive(location, paths) ? "active" : "";

  return (
    <li className={activeClass}>
      <Link
        className={`flex ${flexClass} ${joyrideClass}`}
        to={path}
        title={title}
      >
        {icon}
        <div className="sidebar-label">{label}</div>
      </Link>
    </li>
  );
};

MenuItem.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  location: PropTypes.object.isRequired,
  path: PropTypes.string.isRequired,
  paths: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]).isRequired,
  title: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  className: PropTypes.string,
  joyrideClass: PropTypes.string.isRequired,
};

const Sidebar = ({ isOpen }) => {
  const location = useLocation();

  const {
    Dashboard,
    DataSource,
    Upload,
    STTMapping,
    Administration,
    ToDo,
    AIAsst,
  } = JSON.parse(sessionStorage.getItem("rolesAndPermission") || "{}");

  if (isOpen !== false) {
    return null;
  }

  return (
    <div className="sidebar closed">
      <ul className={`sidebar-menu ${isOpen === false ? "margin-closed" : ""}`}>
        {Dashboard?.canView && (
          <MenuItem
            isOpen={isOpen}
            location={location}
            path="/dashboard"
            paths="/dashboard"
            title="Dashboard"
            icon={<DashboardIcon />}
            label="Dashboard"
            joyrideClass="sidebar-dashboard-joyride-class"
          />
        )}
        {DataSource?.canView && (
          <MenuItem
            isOpen={isOpen}
            location={location}
            path="/data-sources"
            paths="/data-sources"
            title="Data Sources"
            icon={<DataSourceIcon />}
            label="Data Sources"
            joyrideClass="sidebar-datasource-joyride-class"
          />
        )}
        {Upload?.canView && (
          <MenuItem
            isOpen={isOpen}
            location={location}
            path="/upload"
            paths="/upload"
            title="Upload"
            icon={<File className="menu-icon" />}
            label="Upload"
            joyrideClass="sidebar-upload-joyride-class"
          />
        )}
        {STTMapping?.canView && (
          <MenuItem
            isOpen={isOpen}
            location={location}
            path="/view-data"
            paths={["/view-data", "/history-data"]}
            title="View Data"
            icon={<MappingIcon />}
            label="Mapping"
            joyrideClass="sidebar-stt-joyride-class"
          />
        )}

        {/* <MenuItem
          isOpen={isOpen}
          location={location}
          path="/erd"
          paths="/erd"
          title="Reverse Engineering"
          icon={<Network />}
          label="Reverse Engineering"
          joyrideClass="sidebar-ai-joyride-class"
        /> */}

        {AIAsst?.canView && (
          <MenuItem
            isOpen={isOpen}
            location={location}
            path="/falcon-ai"
            paths="/falcon-ai"
            title="Bluegecko AI"
            icon={
              <img
                src={MyLogo}
                alt="My Logo"
                className="menu-icon w-6 h-6"
              />
            }
            label="BlueGecko AI"
            joyrideClass="sidebar-ai-joyride-class"
          />
        )}

        {ToDo?.canView && (
          <MenuItem
            isOpen={isOpen}
            location={location}
            path="/task"
            paths="/task"
            title="Task Management"
            icon={<FaTasks className="text-blue-600 w-5 h-5" />}
            label="Task Management"
            joyrideClass="sidebar-ai-joyride-class"
          />
        )}

        {Administration?.canView && (
          <MenuItem
            isOpen={isOpen}
            location={location}
            path="/admin-interface"
            paths="/admin-interface"
            title="Settings"
            icon={<FaCog className="menu-icon" />}
            label="Admin Settings"
            joyrideClass="sidebar-settings-joyride-class"
          />
        )}
      </ul>
    </div>
  );
};

Sidebar.propTypes = {
  isOpen: PropTypes.bool.isRequired,
};

export default Sidebar;
